/**
 * @module components/XAxisSetting/XAxisSortBy/utils
 * @description X 轴排序上传协议常量与 payload 构造
 */

import { SortByEnum, type TSortByEnum } from '../../common/SortByPanel/SortType';
import type { ICustomMetricSortValueItem } from '../../common/SortByPanel/CustomMetricSort';
import type { IChartsQuickDragItem } from '../../../hooks/useChartsQuickDragList';

/** 上传协议 sortBy 取值 */
export const SORT_BY_UPLOAD = {
  DEFAULT: 'default',
  METRIC: 'metric',
  MANUAL: 'x-axis',
  /** Step 1 占位协议值；Step 2 与后端对齐后可改 */
  DATASET_FIELD: 'datasetField',
} as const;

export interface IXAxisSortState {
  chartSorts: ICustomMetricSortValueItem[];
  xAxisValues: string[];
  quickDragSeed: IChartsQuickDragItem[];
  columnMetrics: unknown[];
}

/** Context sortBy → SortType UI 态（兼容存量 defaultSort / customMetricSort / manual） */
export function toSortUiMode(sortBy: string | undefined): TSortByEnum {
  if (!sortBy || sortBy === SORT_BY_UPLOAD.DEFAULT || sortBy === 'defaultSort') {
    return SortByEnum.Default;
  }
  if (sortBy === SORT_BY_UPLOAD.MANUAL || sortBy === 'manual') {
    return SortByEnum.Manual;
  }
  if (
    sortBy === SORT_BY_UPLOAD.DATASET_FIELD
    || sortBy === SortByEnum.DatasetFieldSort
  ) {
    return SortByEnum.DatasetFieldSort;
  }
  if (
    sortBy === SORT_BY_UPLOAD.METRIC
    || sortBy === 'customMetricSort'
    || sortBy.endsWith('-asc')
    || sortBy.endsWith('-desc')
  ) {
    return SortByEnum.Metric;
  }
  return SortByEnum.Default;
}

/** xAxisValues 归一化为 string[]（兼容存量 { id, value }） */
export function toXAxisValueStrings(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    if (typeof item === 'string' || typeof item === 'number') {
      return String(item);
    }
    if (item && typeof item === 'object') {
      const row = item as { id?: string | number; value?: string | number };
      return String(row.value ?? row.id ?? '');
    }
    return '';
  });
}

export function seedToXAxisValues(seed: IChartsQuickDragItem[]): string[] {
  return seed.map((item) => String(item.value ?? item.id ?? ''));
}

/**
 * 读取指标排序配置 — 兼容旧版仅存 chartSorts 的配置
 * 优先 customMetricSort，不存在时再取 chartSorts
 */
export function resolveMetricSortValue(
  source: Record<string, unknown>,
): ICustomMetricSortValueItem[] {
  if (Array.isArray(source.customMetricSort)) {
    return source.customMetricSort as ICustomMetricSortValueItem[];
  }
  if (Array.isArray(source.chartSorts)) {
    return source.chartSorts as ICustomMetricSortValueItem[];
  }
  return [];
}

/** 从 Context draft/base 读取指标排序（含 draft 删除语义） */
export function resolveMetricSortFromState(
  draft: Record<string, unknown>,
  base: Record<string, unknown>,
  hasDraft: Record<string, boolean>,
): ICustomMetricSortValueItem[] {
  const draftTouched =
    hasDraft.customMetricSort
    || hasDraft.chartSorts
    || 'customMetricSort' in draft
    || 'chartSorts' in draft;

  if (!draftTouched) {
    return resolveMetricSortValue(base);
  }

  const customRemoved =
    hasDraft.customMetricSort && !('customMetricSort' in draft);
  const chartRemoved = hasDraft.chartSorts && !('chartSorts' in draft);
  if (customRemoved && chartRemoved) {
    return [];
  }

  return resolveMetricSortValue(draft);
}

export function buildDefaultSortPayload(): Record<string, unknown> {
  return {
    sortBy: SORT_BY_UPLOAD.DEFAULT,
    chartSorts: undefined,
    customMetricSort: undefined,
    xAxisValues: undefined,
  };
}

export function buildMetricSortPayload(
  chartSorts: ICustomMetricSortValueItem[],
): Record<string, unknown> {
  return {
    sortBy: SORT_BY_UPLOAD.METRIC,
    xAxisValues: undefined,
    chartSorts,
    customMetricSort: chartSorts,
  };
}

export function buildManualSortPayload(
  xAxisValues: string[],
  quickDragSeed: IChartsQuickDragItem[],
): Record<string, unknown> {
  const values = xAxisValues.length > 0
    ? xAxisValues
    : seedToXAxisValues(quickDragSeed);
  return {
    sortBy: SORT_BY_UPLOAD.MANUAL,
    chartSorts: undefined,
    customMetricSort: undefined,
    xAxisValues: values.length > 0 ? values : undefined,
  };
}

export function buildDatasetFieldSortPayload(
  chartSorts: ICustomMetricSortValueItem[] = [],
): Record<string, unknown> {
  return {
    sortBy: SORT_BY_UPLOAD.DATASET_FIELD,
    xAxisValues: undefined,
    customMetricSort: undefined,
    chartSorts,
  };
}

export function buildSortTypeChangePayload(
  mode: TSortByEnum,
  state: IXAxisSortState,
): Record<string, unknown> {
  if (mode === SortByEnum.Default) {
    return buildDefaultSortPayload();
  }

  if (mode === SortByEnum.DatasetFieldSort) {
    const existing = Array.isArray(state.chartSorts) ? state.chartSorts : [];
    const hasFieldShape = existing.some(
      item => typeof item?.aggregate === 'string',
    );
    // 从指标排序切过来时清空，由 DatasetFieldSort 组件默认选中首字段
    return buildDatasetFieldSortPayload(hasFieldShape ? existing : []);
  }

  if (mode === SortByEnum.Metric) {
    // 切到指标时忽略旧 chartSorts（字段排序 key 语义不同，复用会带脏 key）
    let chartSorts: ICustomMetricSortValueItem[] = [];
    if (
      Array.isArray(state.columnMetrics)
      && state.columnMetrics.length > 0
    ) {
      const first = state.columnMetrics[0] as Record<string, unknown>;
      chartSorts = [{
        key: String(first?.column_name || ''),
        value: 'asc',
        table_code: String(first?.table_code || 't0'),
      }];
    }
    return buildMetricSortPayload(chartSorts);
  }

  const computed = seedToXAxisValues(state.quickDragSeed);
  return buildManualSortPayload(computed, state.quickDragSeed);
}
